import React, { useState, useEffect } from 'react'
import { RefreshCw, Database, BarChart3, TrendingUp, Target, Award } from 'lucide-react'
import { bankQuestionService } from '../../services/bankQuestionService_moi'
import { teacherContentService } from '../../services/teacher/TeacherContentService'

interface QuestionStatsProps {
  lop: string
  chuong: string
  bai: string
  teacherId?: string | null // ✅ FIX: Thêm teacherId để load thống kê từ teacher database
  preloadedDatabases?: Record<'TN' | 'DS' | 'TL', Uint8Array | null> | null // ✅ FIX: Nhận preloaded databases để tránh load lại
}

const QuestionStats: React.FC<QuestionStatsProps> = ({ lop, chuong, bai, teacherId, preloadedDatabases }) => {
  const [stats, setStats] = useState<{
    TN: number
    DS: number
    TL: number
    M1: number
    M2: number
    M3: number
  }>({ TN: 0, DS: 0, TL: 0, M1: 0, M2: 0, M3: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [lop, chuong, bai, teacherId])

  const loadStats = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log(`📊 [Phần 4 - Thống kê] Loading question stats for L${lop}_C${chuong}_B${bai}`, teacherId ? `(Teacher: ${teacherId})` : '(System)')
      
      // ✅ FIX: Nếu có teacherId, load thống kê từ teacher database (sử dụng preloaded nếu có)
      if (teacherId) {
        console.log('👨‍🏫 [Phần 4 - Thống kê] Loading stats from teacher database...')
        
        try {
          let tnDb: Uint8Array | null = null
          let dsDb: Uint8Array | null = null
          let tlDb: Uint8Array | null = null

          // ✅ FIX: Sử dụng preloaded databases nếu có
          if (preloadedDatabases) {
            console.log('✅ [Phần 4 - Thống kê] Using preloaded databases')
            tnDb = preloadedDatabases.TN
            dsDb = preloadedDatabases.DS
            tlDb = preloadedDatabases.TL
          } else {
            // ✅ FIX: Load mới với timeout ngắn
            try {
              const serverPromise = teacherContentService.getTeacherDatabases(teacherId, lop, chuong, bai)
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Server timeout')), 3000)
              )
              
              const serverDbs = await Promise.race([serverPromise, timeoutPromise]) as any
              tnDb = serverDbs.TN
              dsDb = serverDbs.DS
              tlDb = serverDbs.TL
            } catch (e) {
              console.warn('⚠️ [Phần 4 - Thống kê] Failed to fetch databases from server:', e)
            }

            // If not found on server, try local IndexedDB (với timeout ngắn)
            if ((!tnDb || tnDb.length === 0) && (!dsDb || dsDb.length === 0) && (!tlDb || tlDb.length === 0)) {
              console.log('⚠️ [Phần 4 - Thống kê] No server databases found, checking local IndexedDB...')
              try {
                const indexedDBPromise = Promise.all([
                  teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TN').catch(() => null),
                  teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'DS').catch(() => null),
                  teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TL').catch(() => null)
                ])
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('IndexedDB timeout')), 2000)
                )
                const localDbs = await Promise.race([indexedDBPromise, timeoutPromise]) as any[]
                tnDb = localDbs[0]
                dsDb = localDbs[1]
                tlDb = localDbs[2]
              } catch (e) {
                console.warn('⚠️ [Phần 4 - Thống kê] Failed to load from IndexedDB:', e)
              }
            }
          }

          // ✅ FIX: Count questions from teacher databases (chỉ nếu có ít nhất 1 database)
          if ((tnDb && tnDb.length > 0) || (dsDb && dsDb.length > 0) || (tlDb && tlDb.length > 0)) {
            console.log('✅ [Phần 4 - Thống kê] Teacher databases found, counting questions...')
            const counts = await bankQuestionService.getQuestionCounts(lop, chuong, bai, teacherId, { TN: tnDb, DS: dsDb, TL: tlDb })
            console.log(`✅ [Phần 4 - Thống kê] Teacher stats loaded:`, counts)
            setStats(counts)
            return // Thành công, không cần fallback
          } else {
            console.warn('⚠️ [Phần 4 - Thống kê] No teacher databases found, falling back to system stats')
          }
        } catch (err) {
          console.error('❌ [Phần 4 - Thống kê] Error loading teacher stats:', err)
          console.warn('⚠️ [Phần 4 - Thống kê] Falling back to system stats')
          // Tiếp tục fallback về system
        }
      }

      // ✅ FIX: Fallback to system database (luôn chạy nếu không có teacherId hoặc teacher database không có)
      console.log('📊 [Phần 4 - Thống kê] Loading stats from system database...')
      const counts = await bankQuestionService.getQuestionCounts(lop, chuong, bai)
      console.log(`📊 [Phần 4 - Thống kê] System stats loaded:`, counts)
      setStats(counts)
    } catch (err) {
      console.error('❌ [Phần 4 - Thống kê] Error loading question stats:', err)
      setError('Không thể tải thống kê câu hỏi')
    } finally {
      setLoading(false)
    }
  }

  const totalQuestions = stats.TN + stats.DS + stats.TL

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 rounded-3xl shadow-2xl p-8 border-2 border-blue-100/50 backdrop-blur-sm">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mr-4" />
          <span className="text-xl text-slate-600 font-semibold">Đang tải thống kê...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-white via-red-50/30 to-red-50/30 rounded-3xl shadow-2xl p-8 border-2 border-red-100/50 backdrop-blur-sm">
        <div className="text-center text-red-600 py-8">
          <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-xl font-semibold mb-4">{error}</p>
          <button
            onClick={loadStats}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
          >
            Thử lại
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 rounded-xl shadow-lg p-4 border border-blue-100">
      {/* Header - Compact */}
      <div className="text-center mb-4">
        <div className="flex items-center justify-center mb-2">
          <BarChart3 className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-xl font-bold text-slate-800">Thống kê câu hỏi</h3>
        </div>
        <p className="text-base text-slate-600">L{lop} - C{chuong} - B{bai}</p>
      </div>

      {/* Total Questions - Compact */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-lg border border-emerald-200 text-center mb-4">
        <div className="text-2xl font-bold text-emerald-600">{totalQuestions}</div>
        <div className="text-emerald-700 font-medium text-base">Tổng câu hỏi</div>
      </div>

      {/* Question Types - Compact Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200 text-center">
          <div className="text-xl font-bold text-emerald-600">{stats.TN}</div>
          <div className="text-sm text-emerald-700 font-medium">TN</div>
        </div>
        
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-center">
          <div className="text-xl font-bold text-blue-600">{stats.DS}</div>
          <div className="text-sm text-blue-700 font-medium">Đ/S</div>
        </div>
        
        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200 text-center">
          <div className="text-xl font-bold text-purple-600">{stats.TL}</div>
          <div className="text-sm text-purple-700 font-medium">TL</div>
        </div>
      </div>

      {/* Difficulty Levels - Compact Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
          <div className="text-xl font-bold text-green-600">{stats.M1}</div>
          <div className="text-sm text-green-700 font-medium">Dễ</div>
        </div>
        
        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-center">
          <div className="text-xl font-bold text-amber-600">{stats.M2}</div>
          <div className="text-sm text-amber-700 font-medium">TB</div>
        </div>
        
        <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-center">
          <div className="text-xl font-bold text-red-600">{stats.M3}</div>
          <div className="text-sm text-red-700 font-medium">Khó</div>
        </div>
      </div>

      {/* Summary - Compact */}
      <div className="text-center">
        {totalQuestions > 0 ? (
          <div className="text-base font-medium text-emerald-600 flex items-center justify-center">
            <span className="mr-1">📊</span>
            Sẵn sàng tạo bài kiểm tra
          </div>
        ) : (
          <div className="text-base font-medium text-amber-600 flex items-center justify-center">
            <span className="mr-1">⚠️</span>
            Chưa có câu hỏi
          </div>
        )}
      </div>
    </div>
  )
}

export default QuestionStats